// Group-aware entity search results — shared by every surface that renders an
// entity-search dropdown (command palette, Roll-Up "Add existing company",
// Portfolio "From database", …).
//
// When a hit belongs to a resolved economic group (enm.economic_group, served
// as `economicGroup` on both /entities and /screener rows), the surface shows
// the GROUP as a header with the matching member entities nested underneath —
// typing "vets4pets" reads as "Pets at Home / Vets4Pets" + its opcos, not as
// twelve interchangeable LTDs. Ungrouped hits stay flat, in result order.

export interface SearchCluster<T> {
  /** Resolved group label, or null for a standalone entity. */
  group: string | null;
  rows: T[];
}

/** Cluster rows by their group label, preserving result order: a cluster sits
 *  where its first member appeared; later members join it. Standalone rows are
 *  singleton clusters. Also returns the flattened row order (clusters
 *  contiguous) for keyboard-navigation index mapping. */
export function clusterByGroup<T>(
  rows: readonly T[],
  groupOf: (row: T) => string | null | undefined,
): { clusters: SearchCluster<T>[]; flat: T[] } {
  const clusters: SearchCluster<T>[] = [];
  const byGroup = new Map<string, SearchCluster<T>>();
  for (const row of rows) {
    const group = groupOf(row) ?? null;
    if (group === null) {
      clusters.push({ group: null, rows: [row] });
      continue;
    }
    const existing = byGroup.get(group);
    if (existing) {
      existing.rows.push(row);
    } else {
      const cluster: SearchCluster<T> = { group, rows: [row] };
      byGroup.set(group, cluster);
      clusters.push(cluster);
    }
  }
  return { clusters, flat: clusters.flatMap((c) => c.rows) };
}

/** The group header line above a cluster's members — one look everywhere. */
export function GroupHeader({ group, count }: { group: string; count: number }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'baseline',
        gap: 6,
        padding: '5px 6px 2px',
        fontSize: 'var(--t-meta)',
        color: 'var(--text-secondary)',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
      }}
    >
      <span
        style={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {group}
      </span>
      <span style={{ color: 'var(--text-tertiary)', fontWeight: 400, textTransform: 'none' }}>
        group · {count} match{count === 1 ? '' : 'es'}
      </span>
    </div>
  );
}

/** Indent applied to member rows under a group header. */
export const GROUP_MEMBER_INDENT = 14;
